import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { buildGrid, neighbourDots } from '../../utils/constellationGrid'
import { COLOR_PICKER_SWATCHES } from './colorPickerConstants'

type Props = {
  className?: string
  style?: CSSProperties
  /** Pixels² per dot. Lower value ⇒ denser field. */
  density?: number
  /** Max px between two dots for a link to render. */
  linkDistance?: number
  /** CSS color for dots. Falls back to --loader-accent. */
  accent?: string
  /** CSS color for baseline links. Falls back to --loader-line. */
  line?: string
  /** Radius in px around cursor where dots respond. */
  interactionRadius?: number
  /** Interaction strength (0..1). */
  intensity?: number
  /** Global alpha multiplier (0..1) for background use. */
  opacity?: number
}

type Dot = {
  idx: number
  x: number
  y: number
  vx: number
  vy: number
  tw: number
  jitter: number
}

const withAlpha = (color: string, alpha: number): string => {
  const clamped = Math.max(0, Math.min(1, alpha))
  const c = color.trim()
  if (c.startsWith('#')) {
    const hex = c.slice(1)
    const full = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex
    const r = parseInt(full.slice(0, 2), 16)
    const g = parseInt(full.slice(2, 4), 16)
    const b = parseInt(full.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${clamped})`
  }
  if (c.startsWith('rgb(')) return c.replace('rgb(', 'rgba(').replace(')', `, ${clamped})`)
  if (c.startsWith('rgba(')) return c.replace(/,[^,]*\)$/, `, ${clamped})`)
  return `color-mix(in srgb, ${c} ${Math.round(clamped * 100)}%, transparent)`
}

/**
 * Ambient constellation field for premium background usage. Dots drift, twinkle,
 * and softly react to the pointer — a subtle twitch and a lattice of thin lines
 * connecting the cursor to nearby stars.
 *
 * Cheap to run: one rAF loop, no per-frame allocations. Respects reduced motion
 * by falling back to a static field.
 *
 * The dot-to-dot link pass uses a spatial grid (O(n) instead of O(n²)) so
 * frame cost is sub-linear in dot count.
 */
export const InteractiveConstellation = ({
  className,
  style,
  density = 1600,
  linkDistance = 128,
  accent,
  line,
  interactionRadius = 168,
  intensity = 1,
  opacity = 1,
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const cs = getComputedStyle(canvas)
    const accentColor = (accent ?? cs.getPropertyValue('--loader-accent').trim()) || COLOR_PICKER_SWATCHES[0]
    const linkColor = (line ?? cs.getPropertyValue('--loader-line').trim()) || cs.getPropertyValue('--border').trim() || 'currentColor'

    const linkDist2 = linkDistance * linkDistance
    const inter2 = interactionRadius * interactionRadius

    let raf = 0
    let running = true
    let visible = !document.hidden
    let cssW = 0
    let cssH = 0
    let dots: Dot[] = []
    const mouse = { x: -9999, y: -9999, active: false }

    const seedDot = (idx: number): Dot => {
      const x = Math.random() * cssW
      const y = Math.random() * cssH
      return {
        idx,
        x,
        y,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        tw: Math.random() * Math.PI * 2,
        jitter: 0.6 + Math.random() * 0.8,
      }
    }

    // Cap lowered from 160 → 100: visually imperceptible above ~80 dots for
    // typical screen sizes, but saves ~37% of link-pass comparisons at the old cap.
    const targetCount = () => Math.max(30, Math.min(100, Math.floor((cssW * cssH) / density)))

    const rebuild = () => {
      const nc = targetCount()
      if (dots.length === 0) {
        dots = Array.from({ length: nc }, (_, i) => seedDot(i))
      } else if (nc > dots.length) {
        for (let i = dots.length; i < nc; i++) dots.push(seedDot(i))
      } else if (nc < dots.length) {
        dots.length = nc
      }
      for (let i = 0; i < dots.length; i++) {
        dots[i].idx = i
      }
    }

    const fit = (w: number, h: number) => {
      if (w < 1 || h < 1) return
      cssW = w
      cssH = h
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      rebuild()
    }

    const draw = (animate: boolean) => {
      if (cssW > 0 && cssH > 0) {
        ctx.clearRect(0, 0, cssW, cssH)

        for (const d of dots) {
          if (animate) {
            d.x += d.vx
            d.y += d.vy
          }

          // gentle wall bounce with a touch of pad so dots don't hug edges
          if (d.x < -8) { d.x = -8; d.vx *= -1 }
          else if (d.x > cssW + 8) { d.x = cssW + 8; d.vx *= -1 }
          if (d.y < -8) { d.y = -8; d.vy *= -1 }
          else if (d.y > cssH + 8) { d.y = cssH + 8; d.vy *= -1 }

          if (animate) d.tw += 0.032

          if (animate && mouse.active) {
            const dx = mouse.x - d.x
            const dy = mouse.y - d.y
            const d2 = dx * dx + dy * dy
            if (d2 < inter2 && d2 > 1) {
              const dist = Math.sqrt(d2)
              const falloff = 1 - dist / interactionRadius
              const pull = falloff * 0.11 * intensity
              d.vx += (dx / dist) * pull
              d.vy += (dy / dist) * pull
              // twitch — subtle random shiver, scaled by proximity
              const shiver = falloff * 0.075 * intensity * d.jitter
              d.vx += (Math.random() - 0.5) * shiver
              d.vy += (Math.random() - 0.5) * shiver
            }
          }

          // damping to prevent runaway; nudge floor speed so dots don't stall
          if (animate) {
            const speed = Math.hypot(d.vx, d.vy)
            if (speed > 0.55) {
              d.vx *= 0.935
              d.vy *= 0.935
            } else if (speed < 0.09) {
              d.vx += (Math.random() - 0.5) * 0.018
              d.vy += (Math.random() - 0.5) * 0.018
            }
          }
        }

        // ── Baseline links between dots — O(n) via spatial grid ───────────
        // Build a grid of cell size == linkDistance. For each dot we only
        // check candidates in its 3×3 cell neighbourhood (9 cells max).
        // Using b.idx > a.idx yields each unordered pair exactly once with
        // zero per-frame Map/Set allocations.
        const grid = buildGrid(dots, linkDistance)
        ctx.lineWidth = 0.7
        for (let i = 0; i < dots.length; i++) {
          const a = dots[i]
          const candidates = neighbourDots(a.x, a.y, grid, linkDistance)
          for (const b of candidates) {
            if (b.idx <= a.idx) continue
            const dx = a.x - b.x
            const dy = a.y - b.y
            const d2 = dx * dx + dy * dy
            if (d2 < linkDist2) {
              const alpha = (1 - d2 / linkDist2) * 0.5 * opacity
              ctx.strokeStyle = withAlpha(linkColor, alpha)
              ctx.beginPath()
              ctx.moveTo(a.x, a.y)
              ctx.lineTo(b.x, b.y)
              ctx.stroke()
            }
          }
        }
        // ──────────────────────────────────────────────────────────────────

        // Cursor-to-star lattice
        if (animate && mouse.active) {
          ctx.lineWidth = 1
          for (const d of dots) {
            const dx = mouse.x - d.x
            const dy = mouse.y - d.y
            const d2 = dx * dx + dy * dy
            if (d2 < inter2) {
              const alpha = (1 - d2 / inter2) * 0.85 * opacity
              ctx.strokeStyle = withAlpha(accentColor, alpha)
              ctx.beginPath()
              ctx.moveTo(mouse.x, mouse.y)
              ctx.lineTo(d.x, d.y)
              ctx.stroke()
            }
          }
        }

        // Dots on top with twinkle
        for (const d of dots) {
          const twinkle = 0.72 + 0.28 * Math.sin(d.tw)
          ctx.fillStyle = withAlpha(accentColor, 0.9 * opacity * twinkle)
          ctx.beginPath()
          ctx.arc(d.x, d.y, 1.65, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }

    const step = () => {
      if (!running) return
      draw(!prefersReducedMotion)
      if (visible && !prefersReducedMotion) raf = requestAnimationFrame(step)
    }

    const schedule = () => {
      if (!running || !visible || prefersReducedMotion || raf) return
      raf = requestAnimationFrame(step)
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const box = entry.contentRect
      fit(box.width, box.height)
    })
    ro.observe(canvas)
    const rect = canvas.getBoundingClientRect()
    fit(rect.width, rect.height)

    const onPointerMove = (event: PointerEvent) => {
      const r = canvas.getBoundingClientRect()
      const x = event.clientX - r.left
      const y = event.clientY - r.top
      if (x < -40 || y < -40 || x > cssW + 40 || y > cssH + 40) {
        mouse.active = false
        return
      }
      mouse.x = x
      mouse.y = y
      mouse.active = true
    }
    const onLeave = () => { mouse.active = false }

    window.addEventListener('pointermove', onPointerMove, { passive: true })
    window.addEventListener('pointerleave', onLeave)
    window.addEventListener('blur', onLeave)
    document.addEventListener('mouseleave', onLeave)

    step()

    const handleVisibilityChange = () => {
      visible = !document.hidden
      if (!visible) {
        cancelAnimationFrame(raf)
        raf = 0
        return
      }
      schedule()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      running = false
      cancelAnimationFrame(raf)
      ro.disconnect()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerleave', onLeave)
      window.removeEventListener('blur', onLeave)
      document.removeEventListener('mouseleave', onLeave)
    }
  }, [density, linkDistance, accent, line, interactionRadius, intensity, opacity])

  return (
    <div
      className={`interactiveConstellation${className ? ` ${className}` : ''}`}
      style={style}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="interactiveConstellation__canvas" />
    </div>
  )
}
